/* eslint-disable no-console */

import React, {useEffect, useMemo, useState} from 'react';
import {
  ActivityIndicator,
  Modal,
  Platform,
  ScrollView,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import ReactNativeModal from 'react-native-modal';
import {useSelector} from 'react-redux';

import Checkbox from '/component/base/Checkbox/Checkbox';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import TextInput from '/component/base/TextInput/TextInput';
import TouchableOpacity from '/component/base/TouchableOpacity/TouchableOpacity';
import {EDGE30_ACTION, EDGE30_BUTTON_TYPE} from '/enum/tealium.enum';
import {variablesEdge365Tasks} from '/redux/selector/remoteConfig.selector';

import {CurrentDateEdge30} from './CurrentDate';
import {styles} from './EdgeDailyTracker.style';
import {
  trackCheckboxToggle_health_plan,
  trackCounterChange,
  trackSaveEvent_edge_30_entry,
} from './EdgeDailyTracker.util';

interface TrackerItem {
  id: string;
  label: string;
  target: number;
  isCheckbox?: boolean;
  dataKey: keyof DailyData;
  userId: string;
  coachId: string;
  info: string;
}

export interface DailyData {
  socialMediaPosts: number;
  invitationsToExploreCoaching: number;
  newFriends: number;
  connections: number;
  attendedWeeklyTraining: number;
  personalHealthPlan: boolean;
  isChallengeCompleted: boolean;
  date: string;
  onNextPress?: () => void;
}
export default function EdgeDailyTracker({
  onNextPress,
  dailyData,
  upsertEdge365ChallengeTasks,
  userId,
  coachId,
  lastPaidRank,
  highestRankDescription,
  coachCurrentRank,
}: {
  onNextPress: () => void;
  dailyData: DailyData;
  upsertEdge365ChallengeTasks: ({payload, coachId}: any) => void;
  userId: '';
  coachId: '';
  lastPaidRank: '';
  highestRankDescription: '';
  coachCurrentRank: '';
}) {
  const {edge30_tasks: rawTrackerData} = useSelector(variablesEdge365Tasks);
  const [modalVisible, setModalVisible] = useState(false);

  const openModal = item => {
    setModalVisible(true);
    setSelectedItem(item);
  };
  const closeModal = () => {
    setModalVisible(false);
  };

  const trackerData: TrackerItem[] = useMemo(() => {
    try {
      if (typeof rawTrackerData === 'string') {
        return JSON.parse(rawTrackerData);
      }
      return Array.isArray(rawTrackerData) ? rawTrackerData : [];
    } catch (error) {
      return [];
    }
  }, [rawTrackerData]);

  const [counters, setCounters] = useState<Record<string, number>>({});
  const [checkboxes, setCheckboxes] = useState<Record<string, boolean>>({});
  const [isChallengeCompleted, setIsChallengeCompleted] = useState(
    dailyData?.isChallengeCompleted,
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [selectedItem, setSelectedItem] = useState<TrackerItem | null>(null);

  const isReadOnly = userId !== coachId;

  useEffect(() => {
    const updatedCounters: Record<string, number> = {};
    const updatedCheckboxes: Record<string, boolean> = {};

    trackerData.forEach(item => {
      const dataKey = item.dataKey;
      const value = dailyData?.[dataKey];
      if (item.isCheckbox) {
        updatedCheckboxes[item.dataKey] = Boolean(value);
      } else {
        updatedCounters[item.dataKey] = Number(value) || 0;
      }
    });

    const countersChanged = Object.keys(updatedCounters).some(
      key => updatedCounters[key] !== counters[key],
    );
    const checkboxesChanged = Object.keys(updatedCheckboxes).some(
      key => updatedCheckboxes[key] !== checkboxes[key],
    );

    if (countersChanged) setCounters(updatedCounters);
    if (checkboxesChanged) setCheckboxes(updatedCheckboxes);

    setIsChallengeCompleted(dailyData?.isChallengeCompleted);
    setHasUnsavedChanges(false);
    setIsSaved(false);
  }, [trackerData, dailyData?.date]);
  const coachIdStr = coachId.toString();

  useEffect(() => {
    const allCompleted = trackerData.every(item => {
      const value = item.isCheckbox
        ? checkboxes[item.dataKey]
        : (counters[item.dataKey] ?? 0) >= item.target;
      return value === true;
    });
    setIsChallengeCompleted(allCompleted);
  }, [counters, checkboxes, trackerData, hasUnsavedChanges]);

  const increment = (dataKey: string) => {
    setCounters(prev => {
      const updated = {...prev, [dataKey]: (prev[dataKey] || 0) + 1};
      const value = updated[dataKey] as number;
      trackCounterChange({
        dataKey,
        action: EDGE30_ACTION.add,
        button_type: EDGE30_BUTTON_TYPE.task_button,
        value,
        coachId: coachIdStr,
        lastPaidRank,
        highestRankDescription,
        coachCurrentRank,
      });
      setHasUnsavedChanges(true);
      setIsSaved(false);
      return updated;
    });
  };

  const decrement = (dataKey: string) => {
    setCounters(prev => {
      const updated = {
        ...prev,
        [dataKey]: Math.max((prev[dataKey] || 0) - 1, 0),
      };
      const value = updated[dataKey] as number;
      trackCounterChange({
        dataKey,
        action: EDGE30_ACTION.remove,
        button_type: EDGE30_BUTTON_TYPE.task_button,
        value,
        coachId: coachIdStr,
        lastPaidRank,
        highestRankDescription,
        coachCurrentRank,
      });
      setHasUnsavedChanges(true);
      setIsSaved(false);
      return updated;
    });
  };

  const handleInputChange = (dataKey: string, value: string) => {
    // Prevent negative numbers and invalid values
    if (!isReadOnly && /^-?\d*$/.test(value)) {
      const numericValue = parseInt(value, 10);
      trackCounterChange({
        dataKey,
        action: EDGE30_ACTION.add,
        button_type: EDGE30_BUTTON_TYPE.manual_entry,
        value: numericValue,
        coachId: coachIdStr,
        lastPaidRank,
        highestRankDescription,
        coachCurrentRank,
      });
      setCounters(prev => {
        const updated = {
          ...prev,
          [dataKey]: isNaN(numericValue) ? 0 : numericValue,
        };
        setHasUnsavedChanges(true);
        setIsSaved(false);
        return updated;
      });
    }
  };

  const toggleCheckbox = (dataKey: string) => {
    setCheckboxes(prev => {
      const updated = {...prev, [dataKey]: !prev[dataKey]};
      setHasUnsavedChanges(true);
      const value = updated[dataKey] ?? false;
      trackCheckboxToggle_health_plan({
        dataKey,
        value,
        coachId: coachIdStr,
        lastPaidRank,
        highestRankDescription,
        coachCurrentRank,
      });
      setIsSaved(false);
      return updated;
    });
  };

  const preparePayload = () => {
    const [yearStr, monthStr, day] = dailyData.date.split('-');
    const dateObj = new Date(
      Number(yearStr),
      Number(monthStr) - 1,
      Number(day),
    );
    const month = dateObj.getMonth() + 1;
    const year = dateObj.getFullYear();
    return {
      socialMediaPosts: counters.socialMediaPosts || 0,
      invitationsToExploreCoaching: counters.invitationsToExploreCoaching || 0,
      newFriends: counters.newFriends || 0,
      connections: counters.connections || 0,
      attendedWeeklyTraining: counters.attendedWeeklyTraining || 0,
      personalHealthPlan: checkboxes.personalHealthPlan || false,
      isChallengeCompleted: isChallengeCompleted || false,
      date: dailyData.date,
      // coachId,
      month,
      year,
    };
  };

  const handleSavePress = async () => {
    trackSaveEvent_edge_30_entry({
      dailyData,
      coachId: coachIdStr,
      lastPaidRank,
      highestRankDescription,
      coachCurrentRank,
    });
    setIsSaved(true);
    try {
      const payload = preparePayload();
      const res = await upsertEdge365ChallengeTasks({payload, coachId});
      setHasUnsavedChanges(false);

      if (isChallengeCompleted && res) {
        onNextPress(); // Proceed to the next action if challenge is completed
      }
    } catch (error) {
      console.error('Error while saving:', error);
    } finally {
      setIsSaved(false);
    }
  };

  return (
    <>
      <ScrollView style={styles.container}>
        <View style={styles.cardContainer}>
          <CurrentDateEdge30 date={dailyData.date} />
          {isChallengeCompleted && (
            <View style={styles.completionContainer}>
              <Text
                allowFontScaling
                maxFontSizeMultiplier={2}
                style={styles.completionText}
                accessible={true}>
                ✓ Day Completed! Great Job!
              </Text>
            </View>
          )}
          {trackerData.map(item => {
            const count = counters[item.dataKey] || 0;

            const isDone = item.isCheckbox
              ? checkboxes[item.dataKey] === true
              : count >= (item.target ?? 0);

            return (
              <View
                key={item.id}
                style={[styles.row, isDone && styles.doneBackground]}>
                {isDone && (
                  <Text
                    allowFontScaling
                    maxFontSizeMultiplier={2}
                    style={styles.checkmark}>
                    ✓
                  </Text>
                )}

                <View style={styles.labelContainer}>
                  <Text
                    allowFontScaling
                    maxFontSizeMultiplier={2}
                    style={[
                      styles.label,
                      isDone ? styles.labelStrikethrough : styles.labelActive,
                    ]}
                    accessible={true}
                    accessibilityLabel={item.label}>
                    {item.label}
                  </Text>
                </View>

                <TouchableOpacity
                  onPress={() => openModal(item)}
                  accessible={true}
                  accessibilityLabel={`${item.label} information icon `}>
                  <Icon
                    name="info"
                    style={[
                      styles.infoIcon,
                      item.label === 'Commit to Health Plan' && {
                        marginRight: 125,
                      },
                    ]}
                  />
                </TouchableOpacity>

                {item.isCheckbox ? (
                  <Checkbox
                    checked={checkboxes[item.dataKey] || false}
                    checkedIconName={
                      checkboxes[item.dataKey]
                        ? 'checkbox-selected'
                        : 'checkbox-unselected'
                    }
                    style={
                      checkboxes[item.dataKey]
                        ? {icon: styles.selectedCheckbox}
                        : {icon: styles.unselectedCheckbox}
                    }
                    onChange={() => toggleCheckbox(item.dataKey)}
                    rectangleCheckbox
                    disabled={isReadOnly}
                  />
                ) : (
                  <View style={styles.counterContainer}>
                    <TouchableOpacity
                      accessible={true}
                      accessibilityLabel={`decrement count`}
                      onPress={() => decrement(item.dataKey)}
                      disabled={count <= 0 || isReadOnly}
                      style={[
                        styles.counterBtn,
                        (count <= 0 || isReadOnly) && styles.disabledBtn,
                      ]}>
                      <Text
                        allowFontScaling
                        maxFontSizeMultiplier={2}
                        style={[
                          styles.counterText,
                          (count <= 0 || isReadOnly) && styles.disabledText,
                        ]}>
                        -
                      </Text>
                    </TouchableOpacity>

                    <TextInput
                      keyboardType="numeric"
                      style={styles.input}
                      value={String(count)}
                      onChangeText={text =>
                        handleInputChange(item.dataKey, text)
                      }
                      maxLength={3}
                      editable={!isReadOnly}
                      disabled={isReadOnly}
                      accessibilityLabel="Number of items"
                      accessibilityHint={
                        isReadOnly
                          ? `This field is read-only. Current value is ${count}`
                          : `Current value is ${count}`
                      }
                    />

                    <TouchableOpacity
                      accessible={true}
                      accessibilityLabel={`increment count`}
                      onPress={() => increment(item.dataKey)}
                      disabled={isReadOnly}
                      style={[
                        styles.counterBtn,
                        isReadOnly && styles.disabledBtn,
                      ]}>
                      <Text
                        allowFontScaling
                        maxFontSizeMultiplier={2}
                        style={[
                          styles.counterText,
                          isReadOnly && styles.disabledText,
                        ]}>
                        +
                      </Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
            );
          })}

          {!isReadOnly && hasUnsavedChanges && (
            <TouchableOpacity
              style={[styles.saveBtnOutside, isSaved && styles.saveBtnDisable]}
              onPress={handleSavePress}
              disabled={isSaved}
              accessible={true}>
              {isSaved ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text
                  allowFontScaling
                  maxFontSizeMultiplier={2}
                  style={styles.saveBtnText}
                  isBold
                  accessible={true}>
                  Save Changes
                </Text>
              )}
            </TouchableOpacity>
          )}
        </View>
      </ScrollView>
      {selectedItem &&
        (Platform.OS === 'web' ? (
          <ReactNativeModal
            isVisible={modalVisible}
            onBackdropPress={closeModal}
            onBackButtonPress={closeModal}
            style={styles.webModalContainer}
            animationIn="slideInRight"
            animationOut="slideOutRight"
            useNativeDriver
            hideModalContentWhileAnimating>
            <View style={styles.webModalView}>
              <View style={styles.base}>
                <View style={styles.webHeaderRow}>
                  <TouchableOpacity
                    style={styles.iconContainer}
                    accessible={true}
                    accessibilityRole="button"
                    accessibilityLabel="Close"
                    onPress={closeModal}>
                    <Icon name="close" style={styles.closeIcon} />
                  </TouchableOpacity>
                  <Text
                    allowFontScaling
                    maxFontSizeMultiplier={2}
                    variant="heading"
                    accessible={true}>
                    {selectedItem?.label}
                  </Text>
                </View>
                <View style={styles.divider} />
                <View style={styles.body}>
                  <Text
                    allowFontScaling
                    maxFontSizeMultiplier={2}
                    style={{base: styles.infoText}}
                    accessible={true}>
                    {selectedItem?.info}
                  </Text>
                </View>
              </View>
            </View>
          </ReactNativeModal>
        ) : (
          <Modal
            animationType="fade"
            transparent={true}
            visible={modalVisible}
            onRequestClose={closeModal}>
            <TouchableWithoutFeedback onPress={closeModal}>
              <View style={styles.modalBackdrop}>
                <TouchableWithoutFeedback>
                  <View style={styles.modalView}>
                    <View style={styles.base}>
                      <View style={styles.header}>
                        <Text
                          allowFontScaling
                          maxFontSizeMultiplier={2}
                          variant="heading"
                          accessible={true}>
                          {selectedItem?.label}
                        </Text>
                      </View>
                      <View style={styles.divider} />
                      <View style={styles.body}>
                        <Text
                          allowFontScaling
                          maxFontSizeMultiplier={2}
                          style={{base: styles.infoText}}
                          accessible={true}>
                          {selectedItem?.info}
                        </Text>
                      </View>
                    </View>
                  </View>
                </TouchableWithoutFeedback>
              </View>
            </TouchableWithoutFeedback>
          </Modal>
        ))}
    </>
  );
}
